import { useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { ConfirmationDialog } from '../../../common/components';
import {
  useCancelSupplierAccountAdjustment,
  useSupplierAccountAdjustments,
} from '../../../hooks/useSupplierLedger';
import { tabPanelSx } from '../constants/supplierLedgerUiConstants';
import useClientPagination from '../hooks/useClientPagination';
import AdjustmentDrawer from './adjustments/AdjustmentDrawer';
import AdjustmentsTable from './adjustments/AdjustmentsTable';
import AdjustmentsToolbar from './adjustments/AdjustmentsToolbar';
import ImportOpeningBalancesDrawer from './adjustments/ImportOpeningBalancesDrawer';

const AdjustmentsTab = ({ providers, providerMap }) => {
  const [filters, setFilters] = useState({ uuid_legal_entity: '', adjustment_type: '', status: '' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const apiFilters = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')),
    [filters],
  );
  const { data: adjustments = [], isLoading } = useSupplierAccountAdjustments(apiFilters);
  const cancelAdjustment = useCancelSupplierAccountAdjustment();
  const adjustmentPagination = useClientPagination(adjustments);
  const providerOptions = useMemo(
    () => providers.map((provider) => ({
      value: provider.uuid,
      label: provider.legal_business_name || provider.company || provider.cuit || 'Proveedor sin nombre',
    })),
    [providers],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelAdjustment = async () => {
    if (!cancelTarget) return;
    await cancelAdjustment.mutateAsync({
      id: cancelTarget.uuid,
      reason: 'Anulación manual desde gestión financiera de proveedores',
    });
    setCancelTarget(null);
  };

  return (
    <Box sx={tabPanelSx}>
      <AdjustmentsToolbar
        filters={filters}
        onChange={handleChange}
        onOpenDrawer={() => setDrawerOpen(true)}
        onOpenImport={() => setImportOpen(true)}
        providerOptions={providerOptions}
      />
      <AdjustmentsTable
        adjustments={adjustments}
        isLoading={isLoading}
        onCancelAdjustment={setCancelTarget}
        pagination={adjustmentPagination}
        providerMap={providerMap}
      />
      <AdjustmentDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} providers={providers} />
      <ImportOpeningBalancesDrawer open={importOpen} onClose={() => setImportOpen(false)} />
      <ConfirmationDialog
        open={Boolean(cancelTarget)}
        title="Anular ajuste"
        message={cancelTarget ? `¿Confirmás anular el ajuste "${cancelTarget.concept}"?` : ''}
        confirmText="Anular"
        confirmVariant="danger"
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelAdjustment}
        isLoading={cancelAdjustment.isPending}
      />
    </Box>
  );
};

export default AdjustmentsTab;
